import { useQueryClient } from "@tanstack/react-query";
import { type FC, type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";

import { OrgEventStreamContext } from "@/contexts/OrgEventStreamContext";
import { createOrgEventDispatcher } from "@/hooks/dispatch-org-event";
import { OrgEventStreamManager } from "@/hooks/org-event-stream-manager";
import { useFlagScopedEventStreamsEnabled } from "@/hooks/use-flag";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { useAuthenticatedUser } from "@/queries/user-queries";

/**
 * Provides the org-scoped event stream to the component tree.
 *
 * Uses a single Provider wrapper regardless of flag state to avoid
 * remounting the entire child tree when the flag resolves. The legacy
 * and scoped streams are managed via hooks inside this component.
 */
export const OrgEventStreamProvider: FC<PropsWithChildren> = ({ children }) => {
    const { value: isScopedEnabled, loading: flagLoading } = useFlagScopedEventStreamsEnabled();

    // Start the legacy (unfiltered) stream immediately while the flag is
    // loading. This avoids a 60s gap where no event stream is active — the
    // flag resolution depends on account + org queries that can be delayed.
    // Once the flag resolves, we either keep legacy (flag=false) or switch
    // to the scoped stream (flag=true). The brief overlap is harmless:
    // event handlers are idempotent (they invalidate queries, not apply deltas).
    const useScoped = !flagLoading && isScopedEnabled;
    const useLegacy = flagLoading || !isScopedEnabled;

    const manager = useScopedEventStream(useScoped);
    useLegacyEventStream(useLegacy);

    return <OrgEventStreamContext.Provider value={manager}>{children}</OrgEventStreamContext.Provider>;
};

/**
 * Hook that manages the scoped OrgEventStreamManager lifecycle.
 * Only active when `enabled` is true. Returns the manager (or null).
 */
function useScopedEventStream(enabled: boolean): OrgEventStreamManager | null {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    const dispatcherRef = useRef<ReturnType<typeof createOrgEventDispatcher> | null>(null);

    const manager = useMemo(() => {
        if (!enabled || !user) {
            return null;
        }

        // Clean up previous dispatcher's debounce timers
        dispatcherRef.current?.cleanup();

        const dispatcher = createOrgEventDispatcher(user.organizationId);
        dispatcherRef.current = dispatcher;

        return new OrgEventStreamManager(api, client, dispatcher.dispatch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, api, client, user?.id, user?.organizationId]);

    useEffect(() => {
        return () => {
            dispatcherRef.current?.cleanup();
            manager?.dispose();
        };
    }, [manager]);

    return manager;
}

/**
 * Hook that runs the legacy unfiltered org-scoped event stream.
 * Only active when `enabled` is true.
 */
const LEGACY_ORG_SCOPE = { case: "organization" as const, value: true as const };

function useLegacyEventStream(enabled: boolean): void {
    const { unsubscribe } = useStreamEvents(enabled ? LEGACY_ORG_SCOPE : undefined);

    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        return onDidChangePrincipal(() => {
            unsubscribe();
            setPrincipal(getPrincipal());
        });
    }, [enabled, setPrincipal, unsubscribe]);
}
